import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  Cloud,
  ExternalLink,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserPlus,
} from "lucide-react";
import { useEffect, useState } from "react";

import { ApiError, googleDriveApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { toast } from "../lib/toast";

export const Route = createFileRoute("/_app/settings/google-drive")({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (user && !user.is_owner) throw redirect({ to: "/settings" });
  },
  component: GoogleDriveSettingsPage,
});

function GoogleDriveSettingsPage() {
  const queryClient = useQueryClient();
  const statusQuery = useQuery({
    queryKey: ["google-drive-status"],
    queryFn: googleDriveApi.status,
  });
  const collaboratorsQuery = useQuery({
    queryKey: ["google-drive-collaborators"],
    queryFn: googleDriveApi.collaborators,
    enabled: statusQuery.data?.connected === true,
  });
  const [collaboratorEmail, setCollaboratorEmail] = useState("");
  const [collaboratorRole, setCollaboratorRole] = useState<"reader" | "writer">("reader");

  useEffect(() => {
    const result = new URLSearchParams(window.location.search).get("google");
    if (!result) return;
    if (result === "connected") {
      toast.success("Google Drive connected and the Biz360 Backups folder was created.");
      queryClient.invalidateQueries({ queryKey: ["google-drive-status"] });
    } else if (result === "cancelled") {
      toast.error("Google Drive connection was cancelled.");
    } else {
      toast.error("Google Drive could not be connected. Please try again.");
    }
    window.history.replaceState({}, "", "/settings/google-drive");
  }, [queryClient]);

  const connectMutation = useMutation({
    mutationFn: googleDriveApi.connect,
    onSuccess: ({ authorization_url }) => window.location.assign(authorization_url),
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : "Could not start Google connection.");
    },
  });

  const testMutation = useMutation({
    mutationFn: googleDriveApi.test,
    onSuccess: ({ folder_name }) => {
      toast.success(`${folder_name} is reachable and writable.`);
      queryClient.invalidateQueries({ queryKey: ["google-drive-status"] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : "Drive test failed.");
      queryClient.invalidateQueries({ queryKey: ["google-drive-status"] });
    },
  });

  const refreshCollaborators = () =>
    queryClient.invalidateQueries({ queryKey: ["google-drive-collaborators"] });
  const addCollaborator = useMutation({
    mutationFn: () => googleDriveApi.addCollaborator(collaboratorEmail, collaboratorRole),
    onSuccess: () => {
      toast.success("Drive access granted.");
      setCollaboratorEmail("");
      setCollaboratorRole("reader");
      refreshCollaborators();
    },
    onError: (error) => toast.error(driveError(error, "Could not grant Drive access.")),
  });
  const updateCollaborator = useMutation({
    mutationFn: ({ id, role }: { id: string; role: "reader" | "writer" }) =>
      googleDriveApi.updateCollaborator(id, role),
    onSuccess: () => {
      toast.success("Drive access updated.");
      refreshCollaborators();
    },
    onError: (error) => toast.error(driveError(error, "Could not update Drive access.")),
  });
  const removeCollaborator = useMutation({
    mutationFn: googleDriveApi.removeCollaborator,
    onSuccess: () => {
      toast.success("Drive access removed.");
      refreshCollaborators();
    },
    onError: (error) => toast.error(driveError(error, "Could not remove Drive access.")),
  });

  const status = statusQuery.data;

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Cloud className="size-5 opacity-70" aria-hidden />
          Google Drive
        </h1>
        <p className="text-sm opacity-60">
          Store Biz360 exports in one dedicated folder without giving access to unrelated Drive
          files.
        </p>
      </header>

      {statusQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm opacity-70">
          <Loader2 className="size-4 animate-spin" /> Loading connection…
        </div>
      ) : statusQuery.error ? (
        <p className="rounded-xl border border-red-400/40 bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-300">
          Could not load Google Drive status: {(statusQuery.error as Error).message}
        </p>
      ) : !status?.configured ? (
        <section className="rounded-xl border border-amber-400/50 bg-amber-500/10 p-5">
          <h2 className="font-semibold">Server configuration required</h2>
          <p className="mt-1 text-sm opacity-75">
            Install the OAuth client ID, client secret, callback URL, and integration encryption key
            on the server before connecting an account.
          </p>
        </section>
      ) : status.connected ? (
        <section className="space-y-4 rounded-xl border border-[var(--border)] p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 size-5 text-emerald-600" aria-hidden />
              <div>
                <h2 className="font-semibold">Connected</h2>
                <p className="text-sm opacity-70">{status.connected_email}</p>
              </div>
            </div>
            <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
              {status.health === "connected" ? "Healthy" : "Needs attention"}
            </span>
          </div>

          <div className="rounded-lg bg-black/[0.035] p-4 dark:bg-white/[0.05]">
            <p className="text-xs font-medium uppercase tracking-wide opacity-50">Backup folder</p>
            <p className="mt-1 font-medium">{status.folder_name}</p>
            {status.folder_url && (
              <a
                href={status.folder_url}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex min-h-11 items-center gap-2 text-sm font-medium text-[var(--accent)] hover:underline"
              >
                Open folder in Google Drive <ExternalLink className="size-4" />
              </a>
            )}
          </div>

          {status.last_error && (
            <p className="rounded-lg bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300">
              {status.last_error}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
              className="inline-flex min-h-11 items-center gap-2 rounded-md border border-[var(--border)] px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              {testMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <ShieldCheck className="size-4" />
              )}
              Test connection
            </button>
            <button
              type="button"
              onClick={() => connectMutation.mutate()}
              disabled={connectMutation.isPending}
              className="inline-flex min-h-11 items-center gap-2 rounded-md border border-[var(--border)] px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              <RefreshCw className="size-4" /> Reconnect account
            </button>
          </div>
        </section>
      ) : (
        <section className="space-y-4 rounded-xl border border-[var(--border)] p-5">
          <div>
            <h2 className="font-semibold">Connect your Google account</h2>
            <p className="mt-1 text-sm opacity-70">
              Google will ask you to approve access. Biz360 will then create a folder named
              <strong> Biz360 Backups</strong> and remember only that folder.
            </p>
          </div>
          <button
            type="button"
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isPending}
            className="inline-flex min-h-11 items-center gap-2 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {connectMutation.isPending && <Loader2 className="size-4 animate-spin" />}
            Connect Google Drive
          </button>
        </section>
      )}

      {status?.connected && (
        <section className="space-y-4 rounded-xl border border-[var(--border)] p-5">
          <div>
            <h2 className="font-semibold">Folder access</h2>
            <p className="mt-1 text-sm opacity-65">
              Grant access to Google accounts without sharing the connected account password. New
              exports inherit access from the backup folder.
            </p>
          </div>

          <form
            className="grid gap-3 sm:grid-cols-[1fr_9rem_auto]"
            onSubmit={(event) => {
              event.preventDefault();
              if (
                collaboratorRole === "writer" &&
                !window.confirm("Editors can modify or delete backup files. Grant Editor access?")
              )
                return;
              addCollaborator.mutate();
            }}
          >
            <label className="text-sm">
              <span className="mb-1 block">Google account email</span>
              <input
                type="email"
                required
                value={collaboratorEmail}
                onChange={(event) => setCollaboratorEmail(event.target.value)}
                className="form-input w-full"
                placeholder="person@example.com"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block">Access</span>
              <select
                value={collaboratorRole}
                onChange={(event) => setCollaboratorRole(event.target.value as "reader" | "writer")}
                className="form-input w-full"
              >
                <option value="reader">Viewer</option>
                <option value="writer">Editor</option>
              </select>
            </label>
            <button
              type="submit"
              disabled={addCollaborator.isPending}
              className="mt-auto inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[var(--accent)] px-4 text-sm font-medium text-white disabled:opacity-50"
            >
              <UserPlus className="size-4" /> Add
            </button>
          </form>

          {collaboratorsQuery.isLoading ? (
            <p className="flex items-center gap-2 text-sm opacity-60">
              <Loader2 className="size-4 animate-spin" /> Checking Drive permissions…
            </p>
          ) : collaboratorsQuery.error ? (
            <p className="text-sm text-red-700 dark:text-red-300">
              {driveError(collaboratorsQuery.error, "Could not read Drive permissions.")}
            </p>
          ) : (
            <div className="divide-y divide-[var(--border)] rounded-lg border border-[var(--border)]">
              {collaboratorsQuery.data?.items.map((permission) => (
                <div
                  key={permission.id}
                  className="flex min-h-16 flex-wrap items-center justify-between gap-3 px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-medium">
                      <span className="truncate">
                        {permission.email ?? permission.display_name ?? permission.type}
                      </span>
                      {permission.is_owner && (
                        <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-300">
                          Owner
                        </span>
                      )}
                      {permission.is_public && (
                        <span className="flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-xs text-red-700 dark:text-red-300">
                          <AlertTriangle className="size-3" /> Public/domain access
                        </span>
                      )}
                    </div>
                    <p className="text-xs capitalize opacity-55">{permission.type}</p>
                  </div>
                  {permission.is_owner ? (
                    <span className="text-sm opacity-60">Full access</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      {!permission.is_public && (
                        <select
                          aria-label={`Access for ${permission.email ?? permission.display_name}`}
                          value={permission.role}
                          disabled={updateCollaborator.isPending}
                          onChange={(event) => {
                            const role = event.target.value as "reader" | "writer";
                            if (
                              role === "writer" &&
                              !window.confirm(
                                "Editors can modify or delete backup files. Continue?",
                              )
                            )
                              return refreshCollaborators();
                            updateCollaborator.mutate({ id: permission.id, role });
                          }}
                          className="form-input min-h-11"
                        >
                          <option value="reader">Viewer</option>
                          <option value="writer">Editor</option>
                        </select>
                      )}
                      <button
                        type="button"
                        aria-label={`Remove access for ${permission.email ?? permission.type}`}
                        disabled={removeCollaborator.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              "Remove this account's access to all Biz360 backup files?",
                            )
                          )
                            removeCollaborator.mutate(permission.id);
                        }}
                        className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md border border-red-400/40 text-red-700 dark:text-red-300"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="rounded-xl border border-[var(--border)] p-5 text-sm">
        <h2 className="font-semibold">What Biz360 can access</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5 opacity-70">
          <li>The Biz360 Backups folder and files Biz360 creates inside it.</li>
          <li>No access to unrelated personal Drive files.</li>
          <li>No Google password is requested or stored.</li>
        </ul>
      </section>
    </div>
  );
}

function driveError(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  return error.reauthRequired
    ? "Sign out and sign in again before changing Drive access."
    : error.message;
}
